import type { ComponentType } from 'react';
import type { TenantCustomization, TenantShellProps } from '../types';
import { keilogAdminTheme } from './shared/providers/admin-theme/keilogAdminTheme';
import { keilogConfig } from './shared/providers/tenantConfig';
import { keilogTheme } from './shared/providers/theme/keilogTheme';
import { getKeilogClinicMenu } from './shared/layouts/menu';
import { keilogClinicRoutes } from './router/routes';
import { keilogComponentOverrides } from './router/componentOverrides';
import { recoverRouteLoad } from './router/recoverRouteLoad';

/**
 * Eager tenant metadata. UI modules remain behind dynamic imports so loading
 * the central registry cannot close a registry -> shell -> core -> registry
 * cycle or pull the KEiLOG screen tree into another tenant's first bundle.
 */
export const keilogCustomization: TenantCustomization = {
  code: 'keilog',
  name: keilogConfig.name,
  description: keilogConfig.description,
  enabled: keilogConfig.enabled,
  theme: keilogTheme,
  adminTheme: keilogAdminTheme,
  config: keilogConfig,
  capabilities: {
    unifiedPortal: keilogConfig.features.unifiedPortal,
    partitionClinicRoutesByServiceType:
      keilogConfig.features.partitionClinicRoutesByServiceType,
    adminSsoMapping: keilogConfig.features.adminSsoMapping,
    recoverStaleSessionHydrationViaPassiveSso:
      keilogConfig.features.recoverStaleSessionHydrationViaPassiveSso,
    // Common spec 30: a lost connection shows a toast, and whatever the
    // operator has typed stays on screen — never a page over the form.
    networkErrorOverlay: false,
  },
  getClinicMenu: getKeilogClinicMenu,
  clinicShell: () =>
    import('./components/templates/RahShell').then(module => ({
      default: module.default as ComponentType<TenantShellProps>,
    })),
  // A screen whose module cannot be fetched (connection down) answers a connection-error page, not a crash.
  clinicIndex: recoverRouteLoad(() => import('./pages/dashboard/RahDashboardPage')),
  clinicRoutes: keilogClinicRoutes.map(route => ({ ...route, component: recoverRouteLoad(route.component) })),
  componentOverrides: Object.fromEntries(
    Object.entries(keilogComponentOverrides).map(([key, loader]) => [key, recoverRouteLoad(loader)])
  ),
};

export default keilogCustomization;
